class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onClearAllFiltersClick = this.onClearAllFiltersClick.bind(this); // Add this line

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.classList.contains('search-input')) {
        this.handleSearch(event);
      } else {
        if(event.target.nodeName == 'INPUT')
          this.onSubmitHandler(event);
      }
    }, 200);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('click', this.updateDetails.bind(this));

    const clearAllFiltersButton = this.querySelector('#clearAllFilters'); // Add this line
    if (clearAllFiltersButton) clearAllFiltersButton.addEventListener('click', this.onClearAllFiltersClick); // Add this line

    
    subscribe(PUB_SUB_EVENTS.facetsUpdate, () => {
      FacetFiltersForm.detailsToggle(true);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {

    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.abortController?.abort();

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const hasCachedSection = sections.every((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      return FacetFiltersForm.filterData.some((element) => element.url === url);
    });

    if (!hasCachedSection) {
      FacetFiltersForm.abortController = new AbortController();
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, FacetFiltersForm.abortController.signal);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, signal) {
    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') throw error;
      })
      .finally(() => {
        if (!signal.aborted) publish(PUB_SUB_EVENTS.facetsUpdate)
      })
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event);
    publish(PUB_SUB_EVENTS.facetsUpdate)
  }

  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(parsedHTML.body.innerHTML);
    }
  }

  static renderProductGridContainer(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const sourceProductGridContainer = parsedHTML.getElementById('ProductGridContainer');
    if (!productGridContainer || !sourceProductGridContainer) return;

    productGridContainer.innerHTML = sourceProductGridContainer.innerHTML;

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
      
      GridSwitcher.updateGridItems()
  }

  static renderProductCount(parsedHTML) {
    const count = parsedHTML.getElementById('ProductCountDesktop')?.innerHTML;
    const container = document.getElementById('ProductCountDesktop');
    if (!count || !container) return;
    container.innerHTML = count;
    container.classList.remove('loading');
  
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements); // .filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    const facetWrapper = parsedHTML.querySelector('#FacetsWrapperDesktop'); 
    const targetFacetWrapper = document.querySelector('#FacetsWrapperDesktop');
    if(facetWrapper && targetFacetWrapper) targetFacetWrapper.innerHTML = facetWrapper.innerHTML

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.mobile-facets__buttons', '.js-discount-filter-label', '.js-discount-filter-options', '.js-discount-filter-active', '.js-discount-filter-clear'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });


    if(document.getElementById('FacetFiltersFormMobile')) document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    this.updateSearchParams(formData);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }
  
  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const attribute = event.currentTarget.dataset.attribute;
    const filterType = event.currentTarget.dataset.filterType;
    const value = event.currentTarget.dataset.value;
    const urlParams = new URLSearchParams(window.location.search);
    const paramValueToRemove = event.currentTarget.dataset.value;
    // Construct the new URL search parameters
    const filteredParams = Array.from(urlParams.entries()).filter(
      ([name, value]) => !(name === attribute && value === paramValueToRemove)
    ); 
    const newSearchParams = new URLSearchParams(filteredParams);

    if (attribute === 'all') {
      if(Antonioli.settings.template == 'search') {
       // Get the current URL
        const url = new URL(window.location.href);
        const qValue = url.searchParams.get('q');
        url.search = '';

        if (qValue) {
            url.searchParams.set('q', qValue);
        }

        window.location.href = url.toString();

      }
      sessionStorage.removeItem('active-facets');
    } else {
      let filter = attribute
      if(value.includes('_')) {
        filter = value.split('_')[0];
      }
      if(filterType) {
        filter = filterType
      }
      
      let activeFacets = JSON.parse(sessionStorage.getItem('active-facets')) || [];
      if(!filteredParams.toString().includes(filter)) {
        activeFacets = activeFacets.filter(item => item !== filter)
        activeFacets.length === 0 ? sessionStorage.removeItem('active-facets') :  sessionStorage.setItem("active-facets", JSON.stringify(activeFacets));
      }
    }
   
    if (attribute === 'all' || attribute.includes('price')) {
      const url =
        event.currentTarget.href.indexOf('?') == -1
          ? ''
          : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      return FacetFiltersForm.renderPage(url);
    }

    FacetFiltersForm.renderPage(this.updateSearchParams(newSearchParams).toString());
    FacetFiltersForm.detailsToggle();
  }

  onClearAllFiltersClick(event) {
    event.preventDefault();
    const searchParams = new URLSearchParams();
    FacetFiltersForm.renderPage(searchParams.toString());
    sessionStorage.removeItem('active-facets');
    FacetFiltersForm.detailsToggle();
  }

  handleSearch(event) {
    let searchValue, filterList, filterListElements;
    const facetsDisplaySidebar = event.target.parentElement.closest('.facets-wrap');

    searchValue = event.target.value.toLowerCase();
    filterList = event.target.closest('fieldset').querySelector('ul');
    filterListElements = filterList.getElementsByTagName('li');
    let counter = parseInt(Array.from(filterListElements).length);
    Array.from(filterListElements).forEach((li) => {
      const span = li.querySelector('label .facet-checkbox__text');
      li.style.display = span.innerHTML.toLowerCase().indexOf(searchValue) > -1 ? '' : 'none';
      counter = span.innerHTML.toLowerCase().indexOf(searchValue) > -1 ? counter : counter - 1;
    });

    if (counter < 12) {
      if (facetsDisplaySidebar) facetsDisplaySidebar.classList.remove('scroll-container');
    } else {
      if (facetsDisplaySidebar) facetsDisplaySidebar.classList.add('scroll-container');
    }
  }

  updateSearchParams(searchParams) {

    if (!searchParams.has('filter.p.m.elastick.filter_meta_macro')) {
      searchParams.delete('filter.p.m.elastick.filter_meta_micro');
    }

    return searchParams;
  }

  updateDetails(event) {
    if(event.target.classList.contains('facets__summary')) {
      if(event.target.closest('at-facets-filters')) {
        const childFilter = FacetFiltersForm.getChildFacetValue(event.target)
        
        if(childFilter) {
          this.updateSessionStorage(event.target, childFilter)
        }
        return;
      }
      if(event.target.parentElement.querySelector('facets-switcher')) {
        const filter = event.target.closest('.js-filter').dataset.filterType
        this.updateSessionStorage(event.target, filter)
        return;
      }
      if(event.target.closest('.js-filter')) {
        const filter = event.target.closest('.js-filter').dataset.filter
        this.updateSessionStorage(event.target, filter)
      }
    }
  }

  static getChildFacetValue(element) {

    if(element) {
     let macrocategory = element.closest('at-facets-filters').dataset.parent;
     document.querySelector(`.macrocategory[data-filter="${macrocategory}"]`).removeAttribute('open');
 
      let filterValues = element.closest('at-facets-filters').dataset.values;
      let filterParent = element.closest('at-facets-filters').dataset.parent;
  
  
      filterValues = filterValues.split('#')
      let childFilter = filterValues.filter(item => item.includes(filterParent))
      if(childFilter.length > 0) {
        childFilter = childFilter.pop().split('_').pop()
      }
      return childFilter

    }
  }

  updateSessionStorage(element, filter) {
    let activeFacets;
    if(sessionStorage.getItem('active-facets') && sessionStorage.getItem('active-facets').length > 0) {
      activeFacets = JSON.parse(sessionStorage.getItem('active-facets'));
      if(!element.closest('details').hasAttribute("open")){
        if(activeFacets.indexOf(filter) == -1) activeFacets.push(filter)
      } else { activeFacets = activeFacets.filter(item => item !== filter) }
    } else {
      !element.closest('details').hasAttribute("open") ? activeFacets = [filter] : activeFacets = [];
    }
    sessionStorage.setItem('active-facets', JSON.stringify(activeFacets))
  }

  static detailsToggle(update = false) {
    let searchParams = window.location.search
    const facetForm = document.getElementById('FacetFiltersForm');
    if(!facetForm) return;
    if (!searchParams.length) return
    const params = new URLSearchParams(searchParams)
    params.delete("sort_by");
    if(params.size > 0) {
      if(sessionStorage.getItem('active-facets') && sessionStorage.getItem('active-facets').length > 0) {
        const lastFacet = JSON.parse(sessionStorage.getItem('active-facets')).pop()
        facetForm.querySelectorAll('.js-filter').forEach(element => {

          if(element.dataset.filterType && element.dataset.filterType == lastFacet) {
            element.querySelector('.facets__disclosure-vertical').setAttribute('open', true)
            return
          }
          if(element.dataset.filter == lastFacet) {
            element.querySelector('.facets__disclosure-vertical').setAttribute('open', true)
          }
        });
        facetForm.querySelectorAll('.micro-category__facet').forEach(element => {
          const childFilter = FacetFiltersForm.getChildFacetValue(element)
          if(Array.isArray(childFilter) && childFilter.length > 0) {
            childFilter.pop().split('_').pop()
          }
          if(childFilter == lastFacet) {
            element.querySelector('.facets__disclosure-vertical').setAttribute('open', true)
          }
        });
      }
    } else {
      facetForm.querySelectorAll('.js-filter').forEach(element => {
        const details = element.querySelector('.facets__disclosure-vertical')
        if(details) {
          element.removeAttribute('open')
          details.removeAttribute('open')
        }
      });
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.detailsToggle();
class PriceRange extends HTMLElement {
  constructor() {
    super();
     this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues(); 
  }

  connectedCallback() {
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form') || document.querySelector('search-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class sortBy extends HTMLElement {
  constructor() {
    super();
    this.isMobile = new DeviceWidth().isMobile;
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.sortByLabel = this.querySelector('.sortby-label');
    this.sortByOptionsWrapper = this.querySelector('.sortby-options');
    this.sortByOptions = this.querySelector('ul')

  }

  connectedCallback() {
    if (!this.isMobile && this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => {
        this.toggleAttribute('open');
      });
    }
    
    Array.from(this.querySelectorAll('input')).forEach(
      input => input.addEventListener('change', (e) => {
        const inputChecked = e.target.dataset.label
        this.sortByLabel.innerHTML = inputChecked
        const drawerData = e.target.closest('.facets-sidebar').getAttribute('data-drawer')
        publish(PUB_SUB_EVENTS.drawerClose, { source: drawerData });
        this.removeAttribute('open')
      }))

    if(FacetFiltersForm.searchParamsPrev) {
      if(FacetFiltersForm.searchParamsPrev.includes('sort_by')) {
        const searchParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
        const sortByValue = searchParams.get("sort_by");
        if(document.querySelector(`[value=${sortByValue}]`)) {
          const inputSelected = document.querySelector(`[value=${sortByValue}]`).dataset.label
          this.sortByLabel.innerHTML = inputSelected 
        }
      }
    }
  }
}

customElements.define('sort-by', sortBy);

class FacetSwitcher extends HTMLElement {
  constructor() {
    super();
    this.grids = {};  // Initialize an object to cache the grid elements
  }

  connectedCallback() {
    if (!this.select) {
      this.select = this.querySelector('select');
      this.select?.addEventListener('change', this.handleSelectChange.bind(this));
    }
    subscribe(PUB_SUB_EVENTS.facetsUpdate, () => {
      this.select = this.querySelector('select');
    });
  }

  handleSelectChange(e) {
      const facetParam = e.target.options[e.target.selectedIndex].dataset.facet;
      this.select.setAttribute('data-value', e.target.value);
      this.select.setAttribute('data-selected-param', facetParam);

      this.select.querySelectorAll('option').forEach((element) => {
        const elementFacet = element.dataset.facet;
        const isActiveFacet = elementFacet === facetParam;

        // Cache or retrieve grid elements based on facet selection
        if (isActiveFacet) {
          if (this.grids[elementFacet]) {
            this.querySelector('[data-facets]').insertAdjacentElement('afterend', this.grids[elementFacet]);
          }
          const activeFacet = this.querySelector(`.facet__element[data-facet="${elementFacet}"]`)
          activeFacet.classList.remove('tw-invisible');
          activeFacet.classList.add('parent-display', 'facets__display-vertical', 'tw-px-[1rem]');
        } else {
          const toRemove = this.querySelector(`.facet__element[data-facet="${elementFacet}"]`)
          if (!this.grids[elementFacet]) {
            this.grids[elementFacet] = toRemove;
          }
          toRemove?.remove()
        }
      });
  }
}

customElements.define('facets-switcher', FacetSwitcher);